import { useTranslate } from 'module/hooks/use-translation';
import { Grid as BaseGrid } from 'module/ui/components/atoms/grid/grid';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { Short01 } from 'module/ui/components/styles';
import {
  faFile,
  faFolder,
  faGlobe,
  faLink,
} from '@fortawesome/pro-regular-svg-icons';
import { WebsiteContent } from 'api/knowledge-base/types';
import { FC, useMemo } from 'react';
import styled from 'styled-components';
import type { ColDef, SelectionChangedEvent } from 'ag-grid-community';

const Grid = styled(BaseGrid)`
  flex: 1;
  .ag-cell {
    border: none !important;
  }
  .ag-cell-focus {
    cursor: pointer;
    border: none !important;
    background-color: transparent !important;
  }
  .ag-selection-checkbox {
    opacity: 1;
  }
  [col-id='checkbox'] {
    padding: 0 !important;
    padding-left: var(--spacing-03) !important;
  }
  --ag-cell-widget-spacing: 16px;
  --ag-row-group-indent-size: 16px;
`;

interface WebsiteFoldersGridProps {
  data: WebsiteContent[];
  onSelectionChange?: (selectedItemInfos: WebsiteContent[]) => void;
  apiDocVersion: boolean;
}

/**
 * Removes query parameters and hash fragments from a URL/path
 */
const removeQueryParamsAndHash = (path: string): string => {
  return path.replace(/[?#].*$/, '');
};

/**
 * Normalizes a path by removing protocol, query params, hash, and trailing slashes
 */
const normalizePath = (path: string): string => {
  return removeQueryParamsAndHash(path)
    .replace(/^https?:\/\//, '')
    .replace(/\/$/, '');
};

/**
 * Parses a path into segments
 */
const parsePathSegments = (path: string): string[] => {
  return normalizePath(path).split('/').filter(Boolean);
};

const getBaseName = (path: string) => {
  return parsePathSegments(path).pop();
};
const Name = styled.span`
  ${Short01};
`;
const getIconForFolder = (apiDocVersion: boolean, isRoot: boolean) => {
  if (isRoot) {
    if (apiDocVersion) {
      return faLink;
    }

    return faGlobe;
  }

  return faFolder;
};

const NameCell: FC<{
  data: {
    type: 'folder' | 'page';
    path: string[];
    name: string;
    itemInfo: WebsiteContent;
  };
  apiDocVersion: boolean;
}> = ({ data, apiDocVersion }) => {
  if (data == null) {
    return <></>;
  }

  return (
    <CenterAlignedBox>
      <FontAwesomeIcon
        icon={
          data.type === 'folder'
            ? getIconForFolder(apiDocVersion, data.path.length === 1)
            : faFile
        }
        size={16}
        style={{ color: '#1D1D1F' }}
      />
      <Name>{data.name}</Name>
    </CenterAlignedBox>
  );
};

export const WebsiteFoldersGrid: FC<WebsiteFoldersGridProps> = ({
  data,
  onSelectionChange,
  apiDocVersion,
}) => {
  const translate = useTranslate();

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    const selectedNodes = event.api.getSelectedNodes();

    const allSelectedItems = new Set<WebsiteContent>();

    selectedNodes.forEach((node) => {
      if (node.data?.itemInfo) {
        allSelectedItems.add(node.data.itemInfo);
      }

      let parent = node.parent;

      while (parent && parent.data?.itemInfo) {
        allSelectedItems.add(parent.data.itemInfo);
        parent = parent.parent;
      }
    });

    onSelectionChange?.(Array.from(allSelectedItems));
  };
  const columnDefs = useMemo<ColDef<any>[]>(
    () => [
      {
        field: 'checkbox',
        headerName: '',
        pinned: 'left',
        lockPinned: true,
        lockPosition: true,
        initialWidth: 24,
        resizable: false,
        sortable: false,
        editable: false,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
      },
    ],
    [],
  );
  const rowData = useMemo(() => {
    const directories = data.filter((item) => item.inode_type === 'directory');
    const directoryPaths = directories.map((item) => normalizePath(item.path));

    const files = data.filter((item) => item.inode_type === 'file');

    const fileData = files.map((item) => {
      let currentPath = normalizePath(item.path);

      while (currentPath) {
        if (directoryPaths.includes(currentPath)) {
          break;
        }
        const pathSegments = parsePathSegments(currentPath);

        currentPath = pathSegments.slice(0, -1).join('/');
      }

      return {
        itemInfo: item,
        id: item.id,
        name: item.meta?.title ?? getBaseName(item.path),
        type: item.inode_type === 'directory' ? 'folder' : 'page',
        path: [...currentPath.split('/'), item.meta?.title],
      };
    });
    const directoryData = directories.map((item) => ({
      itemInfo: item,
      id: item.id,
      name: getBaseName(item.path),
      type: item.inode_type === 'directory' ? 'folder' : 'page',
      path: parsePathSegments(item.path),
    }));

    return [...directoryData, ...fileData];
  }, [data]);

  return (
    <Grid
      rowData={rowData}
      columnDefs={columnDefs}
      treeData
      getDataPath={(data: any) => data.path}
      groupDefaultExpanded={-1}
      onSelectionChanged={handleSelectionChanged}
      groupSelectsChildren={true}
      groupSelectsFiltered={true}
      defaultColDef={{
        filter: false,
        sortable: false,
        resizable: false,
        wrapText: true,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
      }}
      rowSelection="multiple"
      rowMultiSelectWithClick
      suppressContextMenu
      autoGroupColumnDef={{
        headerName:
          translate('knowledge_base.website_folders.table.header.name') ||
          'Name',
        flex: 1,
        cellRendererParams: {
          suppressCount: true,
          innerRenderer: NameCell,
          apiDocVersion,
        },
      }}
    />
  );
};
